import styled from "styled-components";
import { useState, useEffect, createContext } from "react";
import { useLocation } from "react-router-dom";
import {
  format,
  endOfMonth,
  addMonths,
  subMonths,
  eachDayOfInterval,
  startOfMonth,
} from "date-fns";
import { useQueryClient } from "react-query";
import SelectionBar from "../../../selection_bar/SelectionBar";
import TourGroupModal from "../../tour_guides/content/TourGroupModal";
import SchedulePlannerBarContent from "../bar/SchedulePlannerBarContent";
import StaffPlannerGrid from "./staff_planner/StaffPlannerGrid";
import VehiclePlannerGrid from "./vehicle_planner/VehiclePlannerGrid";
import TourGroupsList from "./TourGroupsListWrapper";
import {
  useFetchStaffScheduleByDateRange,
  useFetchVehicleScheduleByDateRange,
} from "@/reactQueryHooks";
import "./style.css";

//----------------------------------------------------------------

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 5px;
  overflow: hidden;
  padding: 5px;
`;

//------------------------------------------------------------------------

export const Context = createContext<{
  user_ids: string[];
  setUser_ids: (ids: string[]) => void;
  vehicle_ids: string[];
  setVehicle_ids: (ids: string[]) => void;
  tourGroupsDate: string;
  setTourGroupsDate: (date: string) => void;
  setTourGroupModalId: (id: string) => void;
  setShowTourGroupModal: (show: boolean) => void;
}>({
  user_ids: [],
  setUser_ids: (ids: string[]) => {},
  vehicle_ids: [],
  setVehicle_ids: (ids: string[]) => {},
  tourGroupsDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
  setTourGroupsDate: (date: string) => {},
  setTourGroupModalId: (id: string) => {},
  setShowTourGroupModal: (show: boolean) => {},
});

const SchedulePlanner = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isStaffRoute = currentPath.includes("/schedule_planner/staff");
  const isVehicleRoute = currentPath.includes("/schedule_planner/vehicles");
  const [isTourGroupModalShown, setShowTourGroupModal] = useState(false);
  const [tourGroupModalId, setTourGroupModalId] = useState<string>("");
  const queryClient = useQueryClient();
  const [tourGroupsDate, setTourGroupsDate] = useState<string>(
    //start of month
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );

  const [targetDate, setTargetDate] = useState<string>(
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [user_ids, setUser_ids] = useState<string[]>([]);
  const [vehicle_ids, setVehicle_ids] = useState<string[]>([]);

  const date_range: string[] = eachDayOfInterval({
    start: new Date(targetDate),
    end: endOfMonth(new Date(targetDate)),
  }).map((date) => format(date, "yyyy-MM-dd"));

  const { refetch: refetchSchedule } = useFetchStaffScheduleByDateRange(
    date_range,
    user_ids
  );

  const { refetch: refetchVehicleSchedule } =
    useFetchVehicleScheduleByDateRange(date_range, vehicle_ids);

  //-----------------------------------------------------------------------

  const handleInvalidateTourGroupsForSchedule = (): void => {
    queryClient.invalidateQueries("TOUR_GROUPS_BY_SPECIFIC_DATE_FOR_SCHEDULE");
  };

  const handleDateChange = (action: "next" | "prev"): void => {
    if (action === "next") {
      //add 1 month
      const newDate = addMonths(new Date(targetDate), 1);

      setTargetDate(format(newDate, "yyyy-MM-dd"));
      setTourGroupsDate(format(newDate, "yyyy-MM-dd"));
      refetchSchedule();
      handleInvalidateTourGroupsForSchedule();
    } else {
      //subtract 1 month
      const newDate = subMonths(new Date(targetDate), 1);

      setTargetDate(format(newDate, "yyyy-MM-dd"));
      setTourGroupsDate(format(newDate, "yyyy-MM-dd"));
      refetchSchedule();
      handleInvalidateTourGroupsForSchedule();
    }
  };

  //------------------------------------------------------------------------

  useEffect(() => {
    const local_user_ids = localStorage.getItem("user_ids");
    if (local_user_ids) {
      setUser_ids(JSON.parse(local_user_ids));
    }
  }, [setUser_ids]);

  useEffect(() => {
    const local_vehicle_ids = localStorage.getItem("vehicle_ids");
    if (local_vehicle_ids) {
      setVehicle_ids(JSON.parse(local_vehicle_ids));
    }
  }, [setUser_ids]);

  //------------------------------------------------------------------------

  return (
    <Context.Provider
      value={{
        user_ids,
        setUser_ids,
        vehicle_ids,
        setVehicle_ids,
        tourGroupsDate,
        setTourGroupsDate,
        setTourGroupModalId,
        setShowTourGroupModal,
      }}
    >
      <Container>
        <SelectionBar>
          <SchedulePlannerBarContent
            date={targetDate}
            setDate={(date: string) => {
              setTargetDate(date);
              setTourGroupsDate(date);
            }}
            handleDateChange={handleDateChange}
            refetch={() => {
              refetchSchedule();
              refetchVehicleSchedule();
              handleInvalidateTourGroupsForSchedule();
            }}
          />
        </SelectionBar>
        <ContentContainer>
          {isStaffRoute && <StaffPlannerGrid targetDate={targetDate} />}
          {isVehicleRoute && <VehiclePlannerGrid targetDate={targetDate} />}
          <TourGroupsList />
          {isTourGroupModalShown && (
            <TourGroupModal
              close={() => setShowTourGroupModal(false)}
              id={tourGroupModalId}
            />
          )}
        </ContentContainer>
      </Container>
    </Context.Provider>
  );
};

export default SchedulePlanner;
